import React, { useState } from "react"
import Papa from "papaparse"
import { db } from "../../firebase-config" // Firestore Config
import { collection, addDoc, getDocs, query, where } from "firebase/firestore"
import Dropzone from "react-dropzone"

const CSVUploader = () => {
  const [file, setFile] = useState(null)
  const [csvData, setCsvData] = useState([])
  const [mappedData, setMappedData] = useState([])
  const [columnMap, setColumnMap] = useState({})
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState("")
  const [step, setStep] = useState(1) // 1: Upload, 2: Map Columns, 3: Preview Data
  const [columns, setColumns] = useState([])
  const [existingPhoneNumbers, setExistingPhoneNumbers] = useState(new Set())

  // Firestore Collection Reference
  const collectionRef = collection(db, "booked-clients") // Change collection name

  // Expected Data Fields
  const expectedFields = ["firstName", "email", "phoneNumber", "address"]
  const requiredFields = ["email", "firstName", "phoneNumber"] // Mandatory fields

  // Handle File Drop
  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0]
    setFile(file)
    parseCSV(file)
  }

  // Parse CSV
  const parseCSV = (file) => {
    const reader = new FileReader()
    reader.onload = ({ target }) => {
      const result = Papa.parse(target.result, {
        header: true,
        skipEmptyLines: true,
      })

      if (result.data.length === 0) {
        setMessage("CSV file is empty or not formatted correctly.")
        return
      }

      setCsvData(result.data)
      setColumns(Object.keys(result.data[0]))
      setStep(2)
    }
    reader.readAsText(file)
  }

  // Handle Column Selection
  const handleColumnMapping = (csvColumn, field) => {
    setColumnMap((prev) => ({ ...prev, [field]: csvColumn }))
  }

  // Function to Format Phone Numbers (Remove (), -, spaces)
  const formatPhoneNumber = (phone) => {
    return phone.replace(/^\+1|\s+|[\s()+-]/g, "").trim() // Removes +1, spaces, (), and -
  }

  // Fetch Existing Phone Numbers from Firestore
  const fetchExistingPhoneNumbers = async () => {
    try {
      const querySnapshot = await getDocs(collectionRef)
      const phoneSet = new Set()
      querySnapshot.forEach((doc) => {
        phoneSet.add(formatPhoneNumber(doc.data().phoneNumber))
      })
      setExistingPhoneNumbers(phoneSet)
    } catch (error) {
      console.error("Error fetching phone numbers:", error)
    }
  }

  // Format Data and Move to Preview Step
  const previewData = async () => {
    await fetchExistingPhoneNumbers()

    // Check if all required fields are mapped
    for (const field of requiredFields) {
      if (!columnMap[field]) {
        setMessage(`Please select a column for ${field} before proceeding.`)
        return
      }
    }

    // Format and filter data
    const formattedData = csvData
      .map((row) => ({
        address: row[columnMap.address] || "",
        bookingInfo: [],
        createdAt: new Date().toISOString(),
        email: row[columnMap.email]?.trim() || "",
        firstName: row[columnMap.firstName]?.trim() || "",
        phoneNumber: formatPhoneNumber(row[columnMap.phoneNumber] || ""),
      }))
      .filter((data, index, self) => {
        // Remove duplicates within CSV and check if phone number exists in Firestore
        const isDuplicateInCSV =
          self.findIndex((item) => item.phoneNumber === data.phoneNumber) !==
          index
        const isDuplicateInDB = existingPhoneNumbers.has(data.phoneNumber)
        return !isDuplicateInCSV && !isDuplicateInDB
      })

    if (formattedData.length === 0) {
      setMessage(
        "All records are duplicates or exist in Firestore. No new data to upload."
      )
      return
    }

    setMappedData(formattedData)
    setStep(3) // Move to preview step
    setMessage("")
  }

  // Upload Data to Firestore
  const handleUpload = async () => {
    setUploading(true)
    setMessage("")

    if (mappedData.length === 0) {
      setMessage("All records are duplicates. No new data uploaded.")
      setUploading(false)
      return
    }

    try {
        for (const data of mappedData) {
            // Query Firestore to check if the phone number already exists
            const phoneQuery = query(collectionRef, where("phoneNumber", "==", data.phoneNumber));
            const phoneSnapshot = await getDocs(phoneQuery);
            const phoneExists = !phoneSnapshot.empty;
          
            if (!phoneExists) {
              await addDoc(collectionRef, data); // Add to Firestore only if phone number doesn't exist
            } else {
            //   console.log(`Skipped duplicate phone number: ${data.phoneNumber}`);
            }
          }
          
          console.log("Final uploaded data:", mappedData);
    
      setMessage("CSV uploaded successfully!")
      setStep(1)
      setFile(null)
      setCsvData([])
    } catch (error) {
      console.error("Error uploading CSV:", error)
      setMessage("Failed to upload CSV.")
    }

    setUploading(false)
  }

  return (
    <div className="flex justify-center items-center bg-gray-100">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full">
        {step === 1 && (
          <>
            <h2 className="text-lg font-semibold mb-3">Upload CSV</h2>
            <Dropzone onDrop={handleDrop} accept=".csv">
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed p-6 text-center cursor-pointer bg-gray-50"
                >
                  <input {...getInputProps()} accept=".csv"/>
                  <p>Drag & Drop CSV here, or click to select</p>
                </div>
              )}
            </Dropzone>
          </>
        )}

        {step === 2 && (
          <div className="w-full">
            <h2 className="text-lg font-semibold mb-3">Map CSV Columns</h2>
            {expectedFields.map((field) => (
              <div key={field} className="mb-3">
                <label className="block text-sm font-medium">{field}</label>
                <select
                  className="border p-2 w-full rounded-md"
                  onChange={(e) => handleColumnMapping(e.target.value, field)}
                >
                  <option value="">Select Column</option>
                  {columns.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(1)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg"
              >
                Back
              </button>
              <button
                onClick={previewData}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg"
              >
                Preview Data
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <>
            <h2 className="text-lg font-semibold mb-3">Preview Data</h2>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-200">
                    <th>No.</th>
                    {expectedFields.map((field) => (
                      <th key={field} className="border p-2 capitalize">
                        {field}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappedData.map((row, index) => (
                    <tr key={index} className="odd:bg-gray-100 even:bg-white">
                      <td className="border p-2">{index + 1}</td>
                      {expectedFields.map((field) => (
                        <td key={field} className="border p-2">
                          {row[field]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setStep(2)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg mt-4"
              >
                Back
              </button>
              <button
                onClick={handleUpload}
                className="bg-yellow-medium_dark text-white px-4 py-2 rounded-lg mt-4"
                disabled={uploading}
                style={uploading ? { cursor: "not-allowed" } : {}}
              >
                {uploading ? "Uploading..." : "Upload to Firestore"}
              </button>
            </div>
          </>
        )}

        {message && <p className="mt-3 text-sm text-red-600">{message}</p>}
      </div>
    </div>
  )
}

export default CSVUploader
